import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { trackPageview, initPageviewTracker } from "@/lib/pageview-tracker";

function PageviewTracker() {
  const location = useLocation();
  useEffect(() => { initPageviewTracker(); }, []);
  useEffect(() => { trackPageview(location.pathname); }, [location.pathname]);
  return null;
}

const Management = lazy(() => import("./pages/Management"));
const ManagementLogin = lazy(() => import("./pages/ManagementLogin"));
const ClientSchedulePortal = lazy(() => import("./pages/ClientSchedulePortal"));
const PublicSchedulePage = lazy(() => import("./pages/PublicSchedulePage"));
const ResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const Admin = lazy(() => import("./pages/Admin"));
const MyProjects = lazy(() => import("./pages/portal/MyProjects"));
const NotFound = lazy(() => import("./pages/NotFound"));


const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <PageviewTracker />
          <Suspense fallback={null}>
            <Routes>
              {/* ESR Management home */}
              <Route path="/" element={<Management />} />
              <Route path="/management" element={<Navigate to="/" replace />} />

              {/* Auth */}
              <Route path="/login" element={<ManagementLogin />} />
              <Route path="/management/login" element={<ManagementLogin />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/portal/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-password" element={<ResetPassword />} />
              <Route path="/portal/forgot-password" element={<ResetPassword />} />

              {/* Calendar / scheduling for trades */}
              <Route path="/portal/schedule/:projectId" element={<ClientSchedulePortal />} />
              <Route path="/portal/projects" element={<MyProjects />} />

              {/* Public tokenized share (no auth) */}
              <Route path="/s/:token" element={<PublicSchedulePage />} />

              {/* Super admin */}
              <Route path="/admin" element={<Admin />} />


              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
